import {fabric} from "fabric";

const SHARE_IMAGE_RUNTIME_PARAMS = ['variant'];
const SHARE_IMAGE_EXTENSION_WITH_TRAILING_EQUALS = /(\.(?:png|jpe?g|webp|gif|svg|avif|tiff?|bmp))=+$/i;

function cleanShareFileParameter(parsedUrl) {
  const file = parsedUrl.searchParams.get('file');

  if (!file) {
    return;
  }

  const cleanFile = file.replace(SHARE_IMAGE_EXTENSION_WITH_TRAILING_EQUALS, '$1');

  if (cleanFile !== file) {
    parsedUrl.searchParams.set('file', cleanFile);
  }
}

// The same image can come with a parameter repeated (prod layouts load /share?...&lightweight=&lightweight=&lightweight=...),
// which gives one image several URLs and so several browser cache entries. PHP reads only the last value of a repeated
// parameter, so keeping that one does not change what the server answers.
function collapseRepeatedParameters(parsedUrl) {
  for (const key of new Set(parsedUrl.searchParams.keys())) {
    const values = parsedUrl.searchParams.getAll(key);
    if (values.length > 1) {
      parsedUrl.searchParams.set(key, values[values.length - 1]);
    }
  }
}

export function stripRuntimeImageUrlParameters(url) {
  if (!url || !url.includes('/share?file=')) {
    return url;
  }

  try {
    const parsedUrl = new URL(url, window.location.origin);

    cleanShareFileParameter(parsedUrl);
    collapseRepeatedParameters(parsedUrl);
    SHARE_IMAGE_RUNTIME_PARAMS.forEach((param) => parsedUrl.searchParams.delete(param));

    return parsedUrl.origin === window.location.origin
      ? `${parsedUrl.pathname}${parsedUrl.search}`
      : parsedUrl.href;
  } catch (e) {
    return url
      .replace(/([?&])variant(=[^&]*)?(&|$)/g, (match, prefix, _value, suffix) => (
        suffix ? prefix : ''
      ))
      .replace(/[?&]$/, '');
  }
}

function addRuntimeImageVariant(url) {
  if (!url || !url.includes('/share?file') || window.isQA) {
    return url;
  }

  try {
    const parsedUrl = new URL(url, window.location.origin);

    cleanShareFileParameter(parsedUrl);
    collapseRepeatedParameters(parsedUrl);

    if (!parsedUrl.searchParams.has('variant')) {
      parsedUrl.searchParams.set('variant', 'avif');
    }

    return parsedUrl.origin === window.location.origin
      ? `${parsedUrl.pathname}${parsedUrl.search}`
      : parsedUrl.href;
  } catch (e) {
    return url.includes('variant=') ? url : `${url}&variant=avif`;
  }
}

// Global override to prevent loading empty/invalid image URLs
const originalFromURL = fabric.Image.fromURL.bind(fabric.Image);
fabric.Image.fromURL = function(url, callback, imgOptions) {
  // Check for empty or invalid URLs
  if (
    typeof url !== 'string'
    || !url
    || url === '/share?file='
    || url.endsWith('/share?file=')
    || url.endsWith('share?file=undefined')
  ) {
    console.warn('[fabric.Image.fromURL] Skipping empty or invalid image URL:', url);
    // Call callback with empty image to avoid breaking the flow
    if (callback) {
      callback(new fabric.Image());
    }
    return;
  }

  url = addRuntimeImageVariant(url);
  
  return originalFromURL(url, callback, imgOptions);
};

const fabricImageCache = new Map();

function cleanUrlAdvanced(url) {
  const forbidden = ['scale', 'lightweight', 'full_resolution'];

  if (!url || url.includes('/share?file')) return url;

  const [base, query] = url.split('?');
  if (!query) {
    const parts = url.split('&');
    const basePart = parts[0];
    const filtered = parts.slice(1).filter(p => !forbidden.includes(p.split('=')[0]));
    return [basePart, ...filtered].join('&');
  }

  const params = new URLSearchParams(query);
  forbidden.forEach(key => params.delete(key));
  const newQuery = params.toString();

  return newQuery ? `${base}?${newQuery}` : base;
}

/**
 * Loads and returns a COPY (clone) of fabric.Image from cache or URL
 * @param {string} src
 * @returns {Promise<fabric.Image>}
 */
export const resolveImageInstance = async (src) => {
  src = cleanUrlAdvanced(src);

  // Check for empty or invalid URLs (e.g., "/share?file=" with no file)
  if (!src || src === '/share?file=' || src.endsWith('/share?file=')) {
    console.warn('[resolveImageInstance] Skipping empty or invalid image URL:', src);
    // Return empty fabric.Image to avoid breaking the canvas
    return new fabric.Image();
  }

  if (fabricImageCache.has(src)) {
    const cachedImage = await fabricImageCache.get(src);
    return new Promise((resolve) => {
      cachedImage.clone((clonedImg) => resolve(clonedImg));
    });
  }

  const imagePromise = new Promise((resolve, reject) => {
    fabric.Image.fromURL(src, (img) => {
      if (!img) return reject(new Error(`Failed to load image: ${src}`));
      resolve(img);
    });
  });

  fabricImageCache.set(src, imagePromise);

  const originalImg = await imagePromise;
  return new Promise((resolve) => {
    originalImg.clone((clonedImg) => resolve(clonedImg));
  });
};

/**
 * see https://jsfiddle.net/r043v/Z7Zg7/
 */
function angle2rect(angle, sx, sy){
  const halfWidth = sx / 2;
  const halfHeight = sy / 2;
  const angleRad = (angle) * (Math.PI / 180) + Math.atan2(sy, sx);
  const dx = Math.cos(angleRad);
  const dy = Math.sin(angleRad);

  const tx1 = (-halfWidth) / dx;
  const tx2 = (halfWidth) / dx;
  const ty1 = (-halfHeight) / dy;
  const ty2 = (halfHeight) / dy;

  const tMinX = Math.min(tx1, tx2);
  const tMaxX = Math.max(tx1, tx2);
  const tMinY = Math.min(ty1, ty2);
  const tMaxY = Math.max(ty1, ty2);

  const tMin = Math.max(tMinX, tMinY);
  const tMax = Math.min(tMaxX, tMaxY);

  if (tMin <= tMax && tMax > 0) {
    const t = tMin > 0 ? tMin : tMax;
    const x = Math.floor(t * dx);
    const y = Math.floor(t * dy);
    return { x: x + sx / 2, y: y + sy /2 };
  }
}

export const setAngleGradientForRect = (angle, width, height) => {
    var odx = 0,
        ody = 0,
        gradient;
    
    gradient = {
        end: angle2rect(angle, width, height)
    };
    
    gradient.start = {
        x: width  - gradient.end.x,
        y: height - gradient.end.y
    }

    const data = {
        x1: gradient.start.x - odx,
        y1: gradient.start.y - ody,
        x2: gradient.end.x   - odx,
        y2: gradient.end.y   - ody,
    }

    return data;
}

/**
 * Warms the HTTP cache for an image that setSrcAsync will load right after a full-resolution
 * metrics load. Applies the same URL normalization and CORS mode as setSrcAsync so the later
 * request is a cache hit instead of a second serialized download.
 */
export function prefetchImage(src) {
  try {
    if (!src || window.isQA) {
      return;
    }

    src = addRuntimeImageVariant(cleanUrlAdvanced(src));

    if (!src || !src.includes('/share?file=')) {
      return;
    }

    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.src = src;
  } catch (e) {
    // best-effort only
  }
}

export function setSrcAsync(fabricImage, src, options = {}) {
  const { 
    useAvif = true, 
    cleanUrl = true 
  } = options;
  
  return new Promise((resolve, reject) => {
    if (cleanUrl) {
      src = cleanUrlAdvanced(src);
    }
    
    if (useAvif) {
      src = addRuntimeImageVariant(src);
    }
    
    fabricImage.setSrc(src, () => {
      resolve(fabricImage);
    }, null, {
      crossOrigin: 'anonymous'
    });
  });
}

function generateRenderKey() {
  return `render:${Math.random().toString(36).slice(2, 10)}`;
}

export function renderTransactionally(canvas, fn, lockKey) {
  if (!canvas) return;

  const key = lockKey ?? generateRenderKey();

  canvas.renderLock.add(key);
  try {
    fn();
  } finally {
    canvas.renderLock.remove(key);
    canvas.requestRenderAll();
  }
}
